import { useState } from 'react';
import { format } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import {
  useCreateProposalMutation,
  useGetContractProposalsQuery,
} from '@/apis/extended/proposalApi';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ACCEPTANCE_DEADLINE_CLAUSE_ID,
  passedAcceptanceDeadline,
} from '@/utils/acceptanceDeadline';
import { parseDateOnly } from '@/utils/dateOnly';

interface AcceptanceDeadlineNoticeProps {
  contract:
    | { _id?: string; timeline?: { offerGoodUntillDate?: string } }
    | null
    | undefined;
}

/**
 * Once the written-acceptance date has passed the contract can't be generated
 * or signed. The Offer Expiration clause is usually already agreed, so it's
 * hidden from the clause list; this proposes a new date on it directly.
 * Proposing reopens negotiation; once the other side accepts, finalize again.
 */
export const AcceptanceDeadlineNotice = ({
  contract,
}: AcceptanceDeadlineNoticeProps) => {
  const contractId = contract?._id?.toString() ?? '';
  const deadline = passedAcceptanceDeadline(contract);
  const { data: proposalsData } = useGetContractProposalsQuery(
    { contractId },
    { skip: !contractId || !deadline }
  );
  const [createProposal, { isLoading }] = useCreateProposalMutation();
  const [newDate, setNewDate] = useState('');

  if (!deadline) return null;

  const pending = (proposalsData?.proposals ?? []).find(
    (p) =>
      p.clauseId === ACCEPTANCE_DEADLINE_CLAUSE_ID && p.status === 'pending'
  );
  const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');

  const handlePropose = async () => {
    if (!newDate || newDate < tomorrow) {
      toast.error('Pick a new date after today.');
      return;
    }
    try {
      await createProposal({
        contractId,
        clauseId: ACCEPTANCE_DEADLINE_CLAUSE_ID,
        clauseHierarchicalId: ACCEPTANCE_DEADLINE_CLAUSE_ID,
        originalValue: contract?.timeline?.offerGoodUntillDate,
        proposedValue: newDate,
        message: 'New written-acceptance date; the previous one has passed.',
      }).unwrap();
      toast.success(
        'New acceptance date proposed. Once the other side accepts it, finalize the contract again.'
      );
      setNewDate('');
    } catch (error: any) {
      toast.error(
        error?.data?.error || 'Could not propose a new acceptance date.'
      );
    }
  };

  return (
    <Alert variant="warning" className="mb-6">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Acceptance deadline has passed</AlertTitle>
      <AlertDescription>
        <p>
          Written acceptance was due by {deadline.toLocaleDateString('en-US')},
          so the contract can't be sent for signature or signed until both sides
          agree a new Offer Expiration Date.
        </p>
        {pending ? (
          <p className="mt-2 font-medium">
            New date proposed:{' '}
            {parseDateOnly(pending.proposedValue)?.toLocaleDateString(
              'en-US'
            ) ?? String(pending.proposedValue)}
            . Waiting for the {pending.responseRequiredFrom} to accept it in the
            negotiation.
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              type="date"
              min={tomorrow}
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-auto"
              aria-label="New acceptance date"
            />
            <Button
              size="sm"
              onClick={handlePropose}
              disabled={!newDate || isLoading}
            >
              {isLoading ? 'Proposing...' : 'Propose new date'}
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
};
